if (!customElements.get('hm-home-product-rail-slider')) {
  class HMHomeProductRailSlider extends HTMLElement {
    constructor() {
      super();

      this.viewport = this.querySelector('[data-product-rail-viewport]');
      this.nextButton = this.querySelector('[data-product-rail-next]');

      this.isPointerDown = false;
      this.pointerId = null;
      this.startX = 0;
      this.startScrollLeft = 0;

      this.handleNextClick = this.handleNextClick.bind(this);
      this.handleScroll = this.handleScroll.bind(this);
      this.handleWheel = this.handleWheel.bind(this);
      this.handlePointerDown = this.handlePointerDown.bind(this);
      this.handlePointerMove = this.handlePointerMove.bind(this);
      this.handlePointerUp = this.handlePointerUp.bind(this);
    }

    connectedCallback() {
      if (!this.viewport) return;

      if (this.nextButton) {
        this.nextButton.addEventListener('click', this.handleNextClick);
        this.nextButton.style.transformOrigin = 'center center';
      }

      this.viewport.addEventListener('scroll', this.handleScroll, { passive: true });
      this.viewport.addEventListener('wheel', this.handleWheel, { passive: false });
      this.viewport.addEventListener('pointerdown', this.handlePointerDown);

      this.updateNextButton();

      requestAnimationFrame(() => {
        this.updateNextButton();
      });
    }

    disconnectedCallback() {
      if (!this.viewport) return;

      if (this.nextButton) {
        this.nextButton.removeEventListener('click', this.handleNextClick);
      }

      this.viewport.removeEventListener('scroll', this.handleScroll);
      this.viewport.removeEventListener('wheel', this.handleWheel);
      this.viewport.removeEventListener('pointerdown', this.handlePointerDown);

      this.removePointerListeners();
    }

    getCardWidth() {
      const firstCard = this.viewport.querySelector('.hm-home-product-rail__card, .hm-home-product-rail__end-card');
      if (!firstCard) return this.viewport.clientWidth * 0.8;

      const styles = window.getComputedStyle(this.viewport);
      const gap = parseFloat(styles.columnGap || styles.gap || 0);

      return firstCard.getBoundingClientRect().width + gap;
    }

    getMaxScrollLeft() {
      const cards = this.viewport.querySelectorAll('.hm-home-product-rail__card, .hm-home-product-rail__end-card');
      const lastCard = cards[cards.length - 1];

      if (!lastCard) {
        return Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth);
      }

      const styles = window.getComputedStyle(this.viewport);
      const endSpace = parseFloat(styles.paddingRight || 0);
      const lastCardRightEdge = lastCard.offsetLeft + lastCard.offsetWidth;

      return Math.max(0, lastCardRightEdge - (this.viewport.clientWidth - endSpace));
    }

    handleNextClick() {
      const firstCard = this.viewport.querySelector('.hm-home-product-rail__card, .hm-home-product-rail__end-card');
      if (!firstCard) return;

      const maxScrollLeft = this.getMaxScrollLeft();
      const atEnd = this.viewport.scrollLeft >= maxScrollLeft;

      if (atEnd) {
        this.viewport.scrollTo({
          left: 0,
          behavior: 'smooth',
        });
        return;
      }

      const cards = Array.from(
        this.viewport.querySelectorAll('.hm-home-product-rail__card, .hm-home-product-rail__end-card')
      );

      const currentCard =
        cards.find((card) => card.offsetLeft + card.clientWidth / 2 > this.viewport.scrollLeft) || firstCard;

      const nextCard = currentCard.nextElementSibling ? currentCard.nextElementSibling : currentCard;

      this.viewport.scrollTo({
        left: Math.min(nextCard.offsetLeft, maxScrollLeft),
        behavior: 'smooth',
      });
    }

    handleScroll() {
      const maxScrollLeft = this.getMaxScrollLeft();

      if (this.viewport.scrollLeft > maxScrollLeft) {
        this.viewport.scrollLeft = maxScrollLeft;
      }

      this.updateNextButton();
    }

    handleWheel(event) {
      const mostlyVertical = Math.abs(event.deltaY) > Math.abs(event.deltaX);
      if (!mostlyVertical) return;
      if (Math.abs(event.deltaY) < 4) return;

      event.preventDefault();
      this.viewport.scrollLeft += event.deltaY;
    }

    handlePointerDown(event) {
      if (event.pointerType === 'mouse' && event.button !== 0) return;

      this.isPointerDown = true;
      this.pointerId = event.pointerId;
      this.startX = event.clientX;
      this.startScrollLeft = this.viewport.scrollLeft;

      this.viewport.classList.add('is-dragging');

      this.viewport.addEventListener('pointermove', this.handlePointerMove);
      this.viewport.addEventListener('pointerup', this.handlePointerUp);
      this.viewport.addEventListener('pointercancel', this.handlePointerUp);
      this.viewport.addEventListener('pointerleave', this.handlePointerUp);
    }

handlePointerMove(event) {
  if (!this.isPointerDown || event.pointerId !== this.pointerId) return;

  const deltaX = event.clientX - this.startX;

  if (Math.abs(deltaX) < 8) return;

  this.viewport.scrollLeft = this.startScrollLeft - deltaX;
}

handlePointerUp(event) {
  if (event.pointerId !== this.pointerId) return;

  this.isPointerDown = false;
  this.pointerId = null;

  this.viewport.classList.remove('is-dragging');
  this.removePointerListeners();
}

    removePointerListeners() {
      if (!this.viewport) return;

      this.viewport.removeEventListener('pointermove', this.handlePointerMove);
      this.viewport.removeEventListener('pointerup', this.handlePointerUp);
      this.viewport.removeEventListener('pointercancel', this.handlePointerUp);
      this.viewport.removeEventListener('pointerleave', this.handlePointerUp);
    }

    updateNextButton() {
      if (!this.nextButton) return;

      const maxScrollLeft = this.getMaxScrollLeft();
      const atEnd = this.viewport.scrollLeft >= maxScrollLeft;
      const canScroll = maxScrollLeft > 0;

      if (!canScroll) {
        this.nextButton.setAttribute('disabled', 'disabled');
        this.nextButton.removeAttribute('data-product-rail-return');
        this.nextButton.setAttribute('aria-label', 'Show next products');
        this.nextButton.style.transform = 'translateY(-50%)';
        return;
      }

      this.nextButton.removeAttribute('disabled');

      if (atEnd) {
        this.nextButton.setAttribute('data-product-rail-return', 'true');
        this.nextButton.setAttribute('aria-label', 'Back to first product');
        this.nextButton.style.transform = 'translateY(-50%) translateX(-5rem) scaleX(-1)';
      } else {
        this.nextButton.removeAttribute('data-product-rail-return');
        this.nextButton.setAttribute('aria-label', 'Show next products');
        this.nextButton.style.transform = 'translateY(-50%)';
      }
    }
  }

  customElements.define('hm-home-product-rail-slider', HMHomeProductRailSlider);
}